<script setup lang="ts">
import { shallowRef, watch } from "vue";

const props = defineProps<{
  apiUrl: string;
  insightId: string;
  token: string;
  active: boolean;
}>();

interface VersionRow {
  version: number;
  message: string | null;
  // Raw IdP `sub` stored in `insight_versions.author`. Kept as the
  // ultimate fallback when the users registry has no row for this sub.
  author: string | null;
  // Display fields enriched server-side from the users registry.
  // Null when the author has never touched the API since the
  // registry was added, or when the IdP didn't supply the claim.
  author_email: string | null;
  author_name: string | null;
  parent_version: number | null;
  is_rollback_of: number | null;
  created_at: string;
}

// Pick the friendliest available author label: name > email > raw sub.
const authorLabel = (row: VersionRow): string | null =>
  row.author_name ?? row.author_email ?? row.author;

type VersionsState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; rows: VersionRow[] }
  | { status: "error"; message: string };

const versionsState = shallowRef<VersionsState>({ status: "idle" });

const loadVersions = async (): Promise<void> => {
  if (versionsState.value.status !== "idle") return;
  versionsState.value = { status: "loading" };
  try {
    const url = `${props.apiUrl.replace(/\/$/, "")}/api/embed/insights/${props.insightId}/versions`;
    const response = await fetch(url, {
      headers: { authorization: `Bearer ${props.token}` },
    });
    if (!response.ok) {
      versionsState.value = {
        status: "error",
        message: `failed to fetch versions: HTTP ${response.status}`,
      };
      return;
    }
    const body = (await response.json()) as { versions?: VersionRow[] };
    versionsState.value = {
      status: "ready",
      rows: Array.isArray(body.versions) ? body.versions : [],
    };
  } catch (e) {
    versionsState.value = {
      status: "error",
      message: e instanceof Error ? e.message : String(e),
    };
  }
};

const formatVersionTimestamp = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
};

// Lazy fetch the first time the tab becomes active
watch(
  () => props.active,
  (isActive) => {
    if (isActive && versionsState.value.status === "idle") {
      void loadVersions();
    }
  },
  { immediate: true }
);

// Reset state and re-fetch if the insight context changes while active
watch(
  () => [props.insightId, props.token, props.apiUrl],
  () => {
    versionsState.value = { status: "idle" };
    if (props.active) void loadVersions();
  }
);
</script>

<template>
  <div class="chrome-tab-body versions-panel" role="tabpanel">
    <p v-if="versionsState.status === 'loading'" class="versions-empty">
      Loading version history…
    </p>
    <p v-else-if="versionsState.status === 'error'" class="versions-error">
      {{ versionsState.message }}
    </p>
    <p
      v-else-if="versionsState.status === 'ready' && !versionsState.rows.length"
      class="versions-empty"
    >
      No version history yet.
    </p>
    <ol
      v-else-if="versionsState.status === 'ready'"
      class="versions-list"
    >
      <li
        v-for="row in versionsState.rows"
        :key="row.version"
        class="version-entry"
      >
        <div class="version-entry-meta">
          <span class="version-entry-num">v{{ row.version }}</span>
          <span
            v-if="authorLabel(row)"
            class="version-entry-author"
            :title="row.author ?? undefined"
          >
            {{ authorLabel(row) }}
          </span>
          <span class="version-entry-dim">
            {{ formatVersionTimestamp(row.created_at) }}
          </span>
          <span
            v-if="row.is_rollback_of !== null"
            class="version-entry-badge"
          >rollback of v{{ row.is_rollback_of }}</span>
        </div>
        <p v-if="row.message" class="version-entry-message">
          {{ row.message }}
        </p>
      </li>
    </ol>
  </div>
</template>

<style scoped>
.versions-panel {
  padding: 8px 0 0;
}
.versions-empty {
  margin: 0;
  padding: 16px 4px;
  color: var(--card-muted, #6b7280);
  font-size: 13px;
}
.versions-error {
  margin: 0;
  padding: 16px 4px;
  color: var(--neg, #b00020);
  font-size: 13px;
}
.versions-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.version-entry {
  border: 1px solid var(--rule, #e5e7eb);
  border-radius: var(--radius, 8px);
  padding: 10px 12px;
  background: var(--card, #fff);
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.version-entry-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 10px;
  font-size: 12px;
}
.version-entry-num {
  font-family: var(--mono, ui-monospace, monospace);
  font-weight: 600;
  color: var(--primary-deep, #044f76);
}
.version-entry-author {
  font-weight: 500;
  color: var(--card-ink, #1a1a1a);
}
.version-entry-dim {
  color: var(--card-muted, #6b7280);
}
.version-entry-badge {
  font-family: var(--mono, ui-monospace, monospace);
  font-size: 10.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 2px 6px;
  border-radius: 4px;
  background: var(--rule-soft, rgba(0, 0, 0, 0.06));
  color: var(--card-muted, #6b7280);
}
.version-entry-message {
  margin: 0;
  font-size: 13px;
  color: var(--card-ink, #1a1a1a);
  line-height: 1.45;
}
</style>